export const ZOOM_SCALES = [0.12, 0.22, 0.38, 0.58, 0.85] as const;

export interface CropRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface CropMeta extends CropRect {
  scale?: number;
  focalX?: number;
  focalY?: number;
}

export function clampRect(rect: CropRect, imgW: number, imgH: number): CropRect {
  let { left, top, width, height } = rect;
  width = Math.max(32, Math.min(Math.floor(width), imgW));
  height = Math.max(32, Math.min(Math.floor(height), imgH));
  left = Math.max(0, Math.min(Math.floor(left), imgW - width));
  top = Math.max(0, Math.min(Math.floor(top), imgH - height));
  return { left, top, width, height };
}

export function ensureSquareRect(rect: CropRect, imgW: number, imgH: number): CropRect {
  const clamped = clampRect(rect, imgW, imgH);
  const size = Math.min(clamped.width, clamped.height, imgW, imgH);
  const cx = clamped.left + clamped.width / 2;
  const cy = clamped.top + clamped.height / 2;
  let left = Math.floor(cx - size / 2);
  let top = Math.floor(cy - size / 2);
  left = Math.max(0, Math.min(left, imgW - size));
  top = Math.max(0, Math.min(top, imgH - size));
  return { left, top, width: size, height: size };
}

export function computeAutoCropRect(
  level: number,
  imgW: number,
  imgH: number,
  focalX = 0.5,
  focalY = 0.5,
): CropRect & { scale: number } {
  const scale = ZOOM_SCALES[level] ?? ZOOM_SCALES[0];
  const side = Math.max(32, Math.floor(Math.min(imgW, imgH) * scale));
  const cx = Math.floor(imgW * focalX);
  const cy = Math.floor(imgH * focalY);
  const left = Math.max(0, Math.min(imgW - side, cx - Math.floor(side / 2)));
  const top = Math.max(0, Math.min(imgH - side, cy - Math.floor(side / 2)));
  return { left, top, width: side, height: side, scale };
}

export function rectToFocal(rect: CropRect, imgW: number, imgH: number) {
  return {
    focalX: (rect.left + rect.width / 2) / imgW,
    focalY: (rect.top + rect.height / 2) / imgH,
  };
}

export type ResizeCorner = 'nw' | 'ne' | 'sw' | 'se';

function clampWithAnchorNW(
  left: number,
  top: number,
  width: number,
  height: number,
  imgW: number,
  imgH: number,
): CropRect {
  const MIN = 32;
  left = Math.max(0, Math.min(Math.floor(left), imgW - MIN));
  top = Math.max(0, Math.min(Math.floor(top), imgH - MIN));
  width = Math.max(MIN, Math.min(Math.floor(width), imgW - left));
  height = Math.max(MIN, Math.min(Math.floor(height), imgH - top));
  return { left, top, width, height };
}

function clampWithAnchorSE(
  right: number,
  bottom: number,
  width: number,
  height: number,
  imgW: number,
  imgH: number,
): CropRect {
  const MIN = 32;
  right = Math.max(MIN, Math.min(Math.floor(right), imgW));
  bottom = Math.max(MIN, Math.min(Math.floor(bottom), imgH));
  width = Math.max(MIN, Math.min(Math.floor(width), right));
  height = Math.max(MIN, Math.min(Math.floor(height), bottom));
  let left = right - width;
  let top = bottom - height;
  if (left < 0) {
    left = 0;
    width = right - left;
  }
  if (top < 0) {
    top = 0;
    height = bottom - top;
  }
  return { left, top, width, height };
}

function clampWithAnchorNE(
  right: number,
  top: number,
  width: number,
  height: number,
  imgW: number,
  imgH: number,
): CropRect {
  const MIN = 32;
  right = Math.max(MIN, Math.min(Math.floor(right), imgW));
  top = Math.max(0, Math.min(Math.floor(top), imgH - MIN));
  width = Math.max(MIN, Math.min(Math.floor(width), right));
  height = Math.max(MIN, Math.min(Math.floor(height), imgH - top));
  let left = right - width;
  if (left < 0) {
    left = 0;
    width = right - left;
  }
  return { left, top, width, height };
}

function clampWithAnchorSW(
  left: number,
  bottom: number,
  width: number,
  height: number,
  imgW: number,
  imgH: number,
): CropRect {
  const MIN = 32;
  left = Math.max(0, Math.min(Math.floor(left), imgW - MIN));
  bottom = Math.max(MIN, Math.min(Math.floor(bottom), imgH));
  width = Math.max(MIN, Math.min(Math.floor(width), imgW - left));
  height = Math.max(MIN, Math.min(Math.floor(height), bottom));
  let top = bottom - height;
  if (top < 0) {
    top = 0;
    height = bottom - top;
  }
  return { left, top, width, height };
}

/** Signed delta for square resize: magnitude = max(|dx|,|dy|). */
function signedSquareDelta(dx: number, dy: number): number {
  const mag = Math.max(Math.abs(dx), Math.abs(dy));
  if (mag === 0) return 0;
  const ref = Math.abs(dx) >= Math.abs(dy) ? dx : dy;
  return ref >= 0 ? mag : -mag;
}

/** Redimensionne depuis un coin en conservant un crop carré. */
export function resizeRectFromCorner(
  start: CropRect,
  corner: ResizeCorner,
  dx: number,
  dy: number,
  imgW: number,
  imgH: number,
): CropRect {
  const sd = signedSquareDelta(dx, dy);
  const right = start.left + start.width;
  const bottom = start.top + start.height;

  let rect: CropRect;
  switch (corner) {
    case 'se':
      rect = clampWithAnchorNW(start.left, start.top, start.width + sd, start.height + sd, imgW, imgH);
      break;
    case 'nw':
      rect = clampWithAnchorSE(right, bottom, start.width - sd, start.height - sd, imgW, imgH);
      break;
    case 'ne':
      rect = clampWithAnchorSW(start.left, bottom, start.width + sd, start.height - sd, imgW, imgH);
      break;
    case 'sw':
      rect = clampWithAnchorNE(right, start.top, start.width - sd, start.height + sd, imgW, imgH);
      break;
  }
  return ensureSquareRect(rect, imgW, imgH);
}

export function resizeRectFieldAnchored(
  rect: CropRect,
  size: number,
  imgW: number,
  imgH: number,
): CropRect {
  return ensureSquareRect(
    clampWithAnchorNW(rect.left, rect.top, size, size, imgW, imgH),
    imgW,
    imgH,
  );
}

export function buildAllLevelRects(
  imgW: number,
  imgH: number,
  focalX: number,
  focalY: number,
  custom: Partial<Record<number, CropRect>>,
): Record<number, CropRect> {
  const out: Record<number, CropRect> = {};
  for (let level = 0; level < 5; level++) {
    out[level] = custom[level]
      ? ensureSquareRect(custom[level]!, imgW, imgH)
      : computeAutoCropRect(level, imgW, imgH, focalX, focalY);
  }
  return out;
}

export function rectToPayload(levels: Record<number, CropRect>) {
  return Object.entries(levels).map(([level, rect]) => ({
    level: Number(level),
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  }));
}
